import { supabase } from '../config/supabase';
import { FinancialPrimaryPayerConflictError, parseFinancialPrimaryPayerConflict, type FinancialPrimaryPayerConflict } from './financialPayerPrimaryConflict';

export interface FinancialPrimaryPayerPreview {
  linkId: string;
  patientId: string;
  patientName: string;
  payerName: string;
  effectiveFrom: string;
  effectiveUntil: string | null;
  isPrimary: boolean;
  targetVersion: string;
  conflicts: FinancialPrimaryPayerConflict[];
}

export class FinancialPrimaryPayerTargetChangedError extends Error {
  constructor() {
    super('Os dados do pagador selecionado foram alterados. Confira os dados atualizados antes de confirmar.');
    this.name = 'FinancialPrimaryPayerTargetChangedError';
  }
}

async function changePrimaryPayer(clientId: string, linkId: string, confirm: boolean, replacePrimaryLinkIds: string[] | null, targetVersion: string | null): Promise<FinancialPrimaryPayerPreview> {
  const { data, error } = await supabase.rpc('financial_a1_set_primary_payer', {
    p_client_id: clientId,
    p_link_id: linkId,
    p_confirm: confirm,
    p_replace_primary_link_ids: replacePrimaryLinkIds,
    p_expected_target_version: targetVersion,
  });
  if (error) {
    if (error.code === 'PPR02') throw new FinancialPrimaryPayerTargetChangedError();
    if (error.code === 'PPR01') {
      const conflicts = parseFinancialPrimaryPayerConflict(error.details);
      if (conflicts) throw new FinancialPrimaryPayerConflictError(conflicts);
    }
    if (error.code === '42501') throw new Error('Somente administradores podem alterar o pagador principal.');
    if (error.code === 'PGRST202' || error.code === '42883') throw new Error('A troca de pagador principal pela lista precisa ser aplicada no banco (47a).');
    throw new Error(error.message || 'Não foi possível alterar o pagador principal.');
  }
  if (!data || typeof data !== 'object'
    || data.linkId !== linkId || typeof data.patientId !== 'string'
    || typeof data.patientName !== 'string' || typeof data.payerName !== 'string'
    || typeof data.effectiveFrom !== 'string'
    || !(data.effectiveUntil === null || typeof data.effectiveUntil === 'string')
    || typeof data.isPrimary !== 'boolean' || !Array.isArray(data.conflicts)
    || typeof data.targetVersion !== 'string' || !data.targetVersion
    || (data.conflicts.length > 0 && !parseFinancialPrimaryPayerConflict(JSON.stringify({ conflicts: data.conflicts })))
    || (confirm && !data.isPrimary)) {
    throw new Error('A troca de pagador principal retornou dados inválidos. Atualize a lista e tente novamente.');
  }
  return data as FinancialPrimaryPayerPreview;
}

export function previewFinancialPrimaryPayer(clientId: string, linkId: string): Promise<FinancialPrimaryPayerPreview> {
  return changePrimaryPayer(clientId, linkId, false, null, null);
}

export function confirmFinancialPrimaryPayer(clientId: string, linkId: string, replacePrimaryLinkIds: string[], targetVersion: string): Promise<FinancialPrimaryPayerPreview> {
  return changePrimaryPayer(clientId, linkId, true, replacePrimaryLinkIds, targetVersion);
}
